import { useState, useEffect } from "react";
import { Modal, Alert } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import Api from "../../Auth/Api";
import { decodeJWT } from "../../Components/jwtUtils";
import Select from "react-select";
import { customStyles } from '../../utils/selectStyles';
import { useAjustes } from "../../Context/AjustesContext";

function ModalNovoAgendamento({ size = 'lg', show, onHide, dataSelecionada, onSuccess, onShowAlerta }) {
    const token = JSON.parse(localStorage.getItem("user_token"));
    const nivel = decodeJWT(token).nivel;
    const { ajustes } = useAjustes();
    // Vê todas as unidades: Admin sempre; Gestor/RH conforme permissão por empresa.
    const veTodasUnidades = nivel === 'Administrador'
        || (nivel === 'Gestor' && Number(ajustes?.gestor_agenda_todas_unidades ?? 0) !== 0)
        || (nivel === 'RH' && Number(ajustes?.rh_agenda_todas_unidades ?? 0) !== 0);

    const { register, handleSubmit, control, setValue, watch, reset, formState: { errors } } = useForm();
    const [cursos, setCursos] = useState([]);
    const [gestoresRh, setGestoresRh] = useState([]);
    const [unidades, setUnidades] = useState([]);
    const [usuarioAtual, setUsuarioAtual] = useState(null);
    const [agendamentosDia, setAgendamentosDia] = useState([]);
    const [horasUtilizadas, setHorasUtilizadas] = useState(0);
    const [showAlertaAntecedencia, setShowAlertaAntecedencia] = useState(false);
    const [showAlertaErro, setShowAlertaErro] = useState(false);
    const [mensagemAlertaErro, setMensagemAlertaErro] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const cursoId = watch('id_curso');
    const dataTreinamento = watch('data_treinamento');
    const unidadeSelecionada = watch('id_unidade');
    const cargaHoraria = watch('carga_horaria');

    useEffect(() => {
        async function fetchData() {
            try {
                // Carregar cursos híbridos
                const cursosResponse = await Api.get('/api/cursos', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCursos(cursosResponse.data);

                // Carregar usuário atual via API
                const usuarioResponse = await Api.get('/api/meu-perfil', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUsuarioAtual({
                    id: usuarioResponse.data.id,
                    nome_completo: usuarioResponse.data.nome_completo || '',
                    unidade: usuarioResponse.data.unidade || ''
                });

                // Admin escolhe o solicitante entre gestores/RH
                if (nivel === 'Administrador') {
                    const gestoresResponse = await Api.get('/api/usuarios-gestores-rh', {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setGestoresRh(gestoresResponse.data);
                }

                // Unidades: quem vê todas usa /api/unidades; restrito usa só as suas.
                const unidadesResponse = await Api.get(veTodasUnidades ? '/api/unidades' : '/api/minhas-unidades', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUnidades(unidadesResponse.data);
            } catch (error) {
                console.error('Erro ao carregar dados:', error);
            }
        }
        fetchData();
    }, [token, nivel, veTodasUnidades]);

    // Preencher carga horária quando curso for selecionado
    useEffect(() => {
        if (cursoId && cursos.length > 0) {
            const curso = cursos.find(c => c.id === parseInt(cursoId?.value ?? cursoId, 10));
            if (curso) {
                setValue('carga_horaria', curso.carga_horaria_pratica || '00:00');
            }
        }
    }, [cursoId, cursos, setValue]);

    // Resetar formulário ao abrir o modal
    useEffect(() => {
        if (!show) {
            return;
        }

        const defaultValues = {
            id_curso: null,
            id_solicitante: null,
            id_unidade: null,
            data_treinamento: '',
            hora_treinamento: '',
            carga_horaria: '',
            observacoes: '',
            alimentacao: false,
        };

        if (dataSelecionada) {
            const data = new Date(dataSelecionada);
            defaultValues.data_treinamento = data.toISOString().split('T')[0];
        }

        reset(defaultValues);
        setShowAlertaAntecedencia(false);
        setShowAlertaErro(false);
        setMensagemAlertaErro('');
        setAgendamentosDia([]);
        setHorasUtilizadas(0);
        setIsLoading(false);
    }, [show, reset, dataSelecionada]);

    // Preencher unidade padrão (não-admin) após abrir o modal: 1 unidade → pré-seleciona;
    // várias → tenta a unidade principal do usuário.
    useEffect(() => {
        if (!show || nivel === 'Administrador' || unidades.length === 0 || unidadeSelecionada) {
            return;
        }

        if (unidades.length === 1) {
            setValue('id_unidade', { value: unidades[0], label: unidades[0] });
            return;
        }

        const unidadePadrao = usuarioAtual && unidades.find(u => u === usuarioAtual.unidade);
        if (unidadePadrao) {
            setValue('id_unidade', { value: usuarioAtual.unidade, label: usuarioAtual.unidade });
        }
    }, [show, usuarioAtual, nivel, unidades, unidadeSelecionada, setValue]);

    // Buscar agendamentos existentes no dia selecionado
    useEffect(() => {
        async function buscarAgendamentosDia() {
            if (!dataTreinamento) {
                setAgendamentosDia([]);
                setHorasUtilizadas(0);
                return;
            }

            try {
                const unidade = unidadeSelecionada?.value || unidadeSelecionada || usuarioAtual?.unidade;

                if (!unidade) {
                    setAgendamentosDia([]);
                    setHorasUtilizadas(0);
                    return;
                }

                const data = new Date(dataTreinamento);
                const mes = data.getMonth() + 1;
                const ano = data.getFullYear();

                // Buscar todos os agendamentos do mês/ano e filtrar por status depois
                const response = await Api.get(
                    `/api/agendamentos-treinamentos?mes=${mes}&ano=${ano}&unidade=${unidade}`,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );

                const agendamentosFiltrados = response.data.filter(a =>
                    a.data_treinamento === dataTreinamento &&
                    a.status !== 'cancelado'
                );

                setAgendamentosDia(agendamentosFiltrados);

                // Calcular horas utilizadas
                let totalHoras = 0;
                agendamentosFiltrados.forEach(ag => {
                    const [horas, minutos] = (ag.carga_horaria || '00:00').split(':').map(Number);
                    totalHoras += horas + (minutos / 60);
                });
                setHorasUtilizadas(totalHoras);
            } catch (error) {
                console.error('Erro ao buscar agendamentos do dia:', error);
                setAgendamentosDia([]);
                setHorasUtilizadas(0);
            }
        }

        buscarAgendamentosDia();
    }, [dataTreinamento, unidadeSelecionada, nivel, usuarioAtual, token]);

    // Calcular horas totais com o novo agendamento
    const calcularHorasTotais = () => {
        if (!cargaHoraria) return horasUtilizadas;

        const [horas, minutos] = cargaHoraria.split(':').map(Number);
        return horasUtilizadas + horas + (minutos / 60);
    };

    const horasTotais = calcularHorasTotais();
    const proximoLimite = horasTotais > 7.5 && horasTotais <= 8;

    // Função para traduzir erros SQL em mensagens humanizadas
    const traduzirErro = (erro, cargaHorariaValue) => {
        const mensagemErro = erro.response?.data?.message || erro.message || 'Erro ao criar agendamento';
        
        // Verificar se é um erro SQL
        if (mensagemErro.includes('SQLSTATE') || mensagemErro.includes('Field') || mensagemErro.includes("doesn't have a default value")) {
            // Erro relacionado a campo obrigatório não preenchido
            if (mensagemErro.includes("doesn't have a default value") || mensagemErro.includes('Field')) {
                // Verificar se é relacionado a carga horária
                if (!cargaHorariaValue || cargaHorariaValue === '00:00' || cargaHorariaValue === '') {
                    return 'O treinamento selecionado não possui carga horária prática cadastrada. Por favor, selecione outro treinamento ou entre em contato com o setor de agendamento via WhatsApp para cadastrar a carga horária.';
                }
                return 'Alguns dados obrigatórios não foram preenchidos corretamente. Verifique todos os campos e tente novamente.';
            }
            
            // Outros erros SQL genéricos
            if (mensagemErro.includes('SQLSTATE')) {
                return 'Ocorreu um erro ao processar sua solicitação. Verifique se todos os campos estão preenchidos corretamente e tente novamente. Se o problema persistir, entre em contato com o suporte.';
            }
        }
        
        // Retornar mensagem original se não for erro SQL
        return mensagemErro;
    };

    const validarAntecedencia = (dataAgendamento, horaAgendamento) => {
        const agora = new Date();
        const dataHoraAgendamento = new Date(`${dataAgendamento}T${horaAgendamento}`);
        const diferencaHoras = (dataHoraAgendamento - agora) / (1000 * 60 * 60);
        
        return diferencaHoras < 48;
    };

    const exibirAlertaErro = (mensagemErro) => {
        if (onShowAlerta) {
            onShowAlerta(mensagemErro, size);
            return;
        }

        setMensagemAlertaErro(mensagemErro);
        setShowAlertaErro(true);
    };

    const onSubmit = async (data) => {
        try {
            // Validar carga horária antes de enviar
            if (!data.carga_horaria || data.carga_horaria === '00:00' || data.carga_horaria === '') {
                const mensagemErro = 'O treinamento selecionado não possui carga horária prática cadastrada. Por favor, selecione outro treinamento ou entre em contato com o setor de agendamento via WhatsApp para cadastrar a carga horária.';
                exibirAlertaErro(mensagemErro);
                return;
            }

            // Verificar se a data está dentro de 48 horas (apenas NÃO administradores são bloqueados)
            const dentroDe48h = validarAntecedencia(data.data_treinamento, data.hora_treinamento);
            
            if (dentroDe48h && nivel !== 'Administrador') {
                // Para não administradores, bloquear completamente
                setShowAlertaAntecedencia(true);
                return;
            }

            // Se passou da validação, envia
            await enviarAgendamento(data);
        } catch (error) {
            const mensagem = traduzirErro(error, data.carga_horaria);
            exibirAlertaErro(mensagem);
        }
    };

    const enviarAgendamento = async (data) => {
        setIsLoading(true);
        try {
            const payload = {
                id_curso: data.id_curso,
                data_treinamento: data.data_treinamento,
                hora_treinamento: data.hora_treinamento,
                carga_horaria: data.carga_horaria,
                observacoes: data.observacoes || null,
                alimentacao: data.alimentacao || false
            };

            // Admin pode escolher o solicitante; demais níveis são o próprio usuário (backend resolve).
            if (nivel === 'Administrador' && data.id_solicitante) {
                payload.id_solicitante = data.id_solicitante.value || data.id_solicitante;
            }
            // Unidade é enviada por todos os níveis (Gestor/RH/Admin).
            if (data.id_unidade) {
                payload.id_unidade = data.id_unidade.value || data.id_unidade;
            }

            await Api.post('/api/agendamentos-treinamentos/novo', payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Limpar formulário
            reset();
            
            onSuccess();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
        <Modal size={size} show={show && !showAlertaAntecedencia} onHide={onHide} backdrop="static">
            <Modal.Header closeButton>
                <span className="fw-semibold fs-5 text-primary">Novo Agendamento</span>
            </Modal.Header>
            <Modal.Body className="bg-gray-100 p-4">
                <form onSubmit={handleSubmit(onSubmit)}>
                    {/* Sinalizadores de agendamentos existentes */}
                    {dataTreinamento && (
                        <>
                            {agendamentosDia.length > 0 ? (
                                <Alert variant={horasTotais > 8 && nivel !== 'Administrador' ? 'danger' : proximoLimite ? 'warning' : 'info'} className="mb-3">
                                    <div className="d-flex align-items-start">
                                        <i className={`bi ${horasTotais > 8 && nivel !== 'Administrador' ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill'} me-2 mt-1`}></i>
                                        <div className="flex-grow-1">
                                            <strong className="d-block mb-2">
                                                {horasTotais > 8 && nivel !== 'Administrador'
                                                    ? 'Limite de 8 horas excedido!'
                                                    : proximoLimite
                                                        ? 'Atenção: Próximo do limite de 8 horas'
                                                        : 'Agendamentos existentes neste dia'}
                                            </strong>
                                            <div className="mb-2">
                                                <small>
                                                    <strong>Horas já agendadas:</strong> {horasUtilizadas.toFixed(2).replace('.', ',')}h
                                                    {cargaHoraria && (
                                                        <> → <strong>Total com este agendamento:</strong> {horasTotais.toFixed(2).replace('.', ',')}h</>
                                                    )}
                                                </small>
                                            </div>
                                            <div className="mt-2">
                                                <small className="d-block mb-1"><strong>Agendamentos existentes:</strong></small>
                                                <div className="ms-3">
                                                    {agendamentosDia.map((ag, idx) => (
                                                        <div key={idx} className="small mb-1">
                                                            • {ag.nome_curso} - {ag.hora_treinamento?.substring(0, 5)} ({ag.carga_horaria})
                                                            <span className={`badge ms-2 ${ag.status === 'pendente' ? 'bg-warning' : ag.status === 'confirmado' ? 'bg-success' : 'bg-secondary'}`}>
                                                                {ag.status === 'pendente' ? 'Pendente' : ag.status === 'confirmado' ? 'Confirmado' : ag.status}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Alert>
                            ) : horasUtilizadas > 0 && (
                                <Alert variant="success" className="mb-3">
                                    <div className="d-flex align-items-start">
                                        <i className="bi bi-check-circle-fill me-2 mt-1"></i>
                                        <div className="flex-grow-1">
                                            <strong className="d-block mb-1">Dia disponível</strong>
                                            <small>
                                                {horasUtilizadas > 0 && (
                                                    <>Horas utilizadas: {horasUtilizadas.toFixed(2).replace('.', ',')}h</>
                                                )}
                                            </small>
                                        </div>
                                    </div>
                                </Alert>
                            )}
                        </>
                    )}

                    <div className="row">
                        {/* Solicitante */}
                        <div className="col-12 col-md-12 mb-3">
                            <label className="form-label text-primary fw-medium">Solicitante</label>
                            {nivel === 'Administrador' ? (
                                <Controller
                                    name="id_solicitante"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={gestoresRh.map(u => ({
                                                value: u.id,
                                                label: `${u.nome_completo} (${u.nivel}) - ${u.unidade}`
                                            }))}
                                            styles={customStyles}
                                            placeholder="Selecione o solicitante"
                                            noOptionsMessage={() => 'Nenhum solicitante encontrado'}
                                        />
                                    )}
                                />
                            ) : (
                                <input
                                    type="text"
                                    className="form-control border-new-light"
                                    value={usuarioAtual?.nome_completo || ''}
                                    readOnly
                                />
                            )}
                        </div>

                        {/* Treinamento */}
                        <div className="col-12 col-md-12 mb-3">
                            <label className="form-label text-primary fw-medium">Treinamento</label>
                            <Controller
                                name="id_curso"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={cursos.map(c => ({
                                            value: c.id,
                                            label: c.nome
                                        }))}
                                        styles={customStyles}
                                        placeholder="Selecione o treinamento"
                                        onChange={(value) => {
                                            field.onChange(value);
                                            if (value) {
                                                const curso = cursos.find(c => c.id === value.value);
                                                if (curso) {
                                                    setValue('carga_horaria', curso.carga_horaria_pratica || '00:00');
                                                }
                                            }
                                        }}
                                        noOptionsMessage={() => 'Nenhum treinamento encontrado'}
                                    />
                                )}
                            />
                            {errors.id_curso && (
                                <span className="text-danger fs-7">Treinamento é obrigatório.</span>
                            )}
                            <small className="text-muted d-block mt-2">
                                Não encontrou o treinamento desejado?{" "}
                                <a
                                    href="https://wa.me/5516996486742"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary"
                                >
                                    Solicite via WhatsApp
                                </a>
                            </small>
                        </div>

                        {/* Carga Horária */}
                        <div className="col-12 col-md-4 mb-3">
                            <label className="form-label text-primary fw-medium">Carga Horária Prática</label>
                            <input
                                type="text"
                                className="form-control border-new-light"
                                {...register('carga_horaria', { required: true })}
                                readOnly
                                placeholder="00:00"
                            />
                        </div>

                        {/* Dia do Treinamento */}
                        <div className="col-12 col-md-4 mb-3">
                            <label className="form-label text-primary fw-medium">Dia do Treinamento</label>
                            <input
                                type="date"
                                className="form-control border-new-light"
                                {...register('data_treinamento', { required: true })}
                            />
                            {errors.data_treinamento && (
                                <span className="text-danger fs-7">Data é obrigatória.</span>
                            )}
                        </div>

                        {/* Horário */}
                        <div className="col-12 col-md-4 mb-3">
                            <label className="form-label text-primary fw-medium">Horário</label>
                            <input
                                type="time"
                                className="form-control border-new-light w-100"
                                {...register('hora_treinamento', { required: true })}
                            />
                            {errors.hora_treinamento && (
                                <span className="text-danger fs-7">Horário é obrigatório.</span>
                            )}
                        </div>

                        {/* Unidade */}
                        <div className="col-12 col-md-6 mb-3">
                            <label className="form-label text-primary fw-medium">Unidade</label>
                            <Controller
                                name="id_unidade"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={unidades.map(u => ({
                                            value: u,
                                            label: u
                                        }))}
                                        styles={customStyles}
                                        placeholder="Selecione a unidade"
                                        noOptionsMessage={() => 'Nenhuma unidade disponível'}
                                    />
                                )}
                            />
                        </div>

                        {/* Observações */}
                        <div className="col-12 mb-3">
                            <label className="form-label text-primary fw-medium">Observações (opcional)</label>
                            <textarea
                                className="form-control border-new-light"
                                rows="3"
                                {...register('observacoes')}
                                placeholder="Número de participantes, local físico, necessidades específicas..."
                            />
                        </div>

                        {/* Alimentação */}
                        <div className="col-12 mb-3">
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="alimentacao"
                                    {...register('alimentacao')}
                                />
                                <label className="form-check-label" htmlFor="alimentacao">
                                    <i className="bi bi-utensils me-2 text-primary"></i>
                                    Alimentação
                                </label>
                            </div>
                        </div>
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer className="justify-content-end">
                <div className="d-flex justify-content-end">
                    <button type="button" className="btn me-2" onClick={onHide} disabled={isLoading}>
                        Cancelar
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSubmit(onSubmit)} disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Solicitando...
                            </>
                        ) : (
                            'Solicitar'
                        )}
                    </button>
                </div>
            </Modal.Footer>
        </Modal>

        {/* Modal de Alerta de Antecedência */}
        <Modal size='lg' show={showAlertaAntecedencia} onHide={() => setShowAlertaAntecedencia(false)}>
            <Modal.Header closeButton>
                <span className="fw-semibold fs-4 text-danger">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    Atenção: Prazo de Antecedência
                </span>
            </Modal.Header>
            <Modal.Body>
                <div className="alert alert-danger d-flex align-items-start mb-0" role="alert">
                    <i className="bi bi-clock-history fs-3 me-3 mt-1"></i>
                    <div>
                        <h6 className="alert-heading mb-2 fw-bold">Agendamento não permitido</h6>
                        <p className="mb-2">
                            <strong>Os treinamentos precisam ser solicitados com no mínimo 48 horas de antecedência.</strong>
                        </p>
                        <p className="mb-0 small text-muted">
                            Por favor, selecione uma data e horário com pelo menos 48 horas de antecedência ou entre em contato com o setor de agendamento.
                        </p>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-center bg-light">
                <button 
                    type="button" 
                    className="btn btn-primary px-5 py-2" 
                    onClick={() => setShowAlertaAntecedencia(false)}
                >
                    <i className="bi bi-check-circle me-2"></i>
                    Entendi
                </button>
            </Modal.Footer>
        </Modal>

        {/* Modal de Alerta de Erro */}
        <Modal size={size} show={showAlertaErro} onHide={() => setShowAlertaErro(false)} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="text-danger">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    Erro
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="alert alert-danger mb-0" role="alert">
                    {mensagemAlertaErro}
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0">
                <button className="btn btn-primary" onClick={() => setShowAlertaErro(false)}>
                    Entendi
                </button>
            </Modal.Footer>
        </Modal>
        </>
    );
}

export default ModalNovoAgendamento;

